import React from "react";

const tiers = [
  ["tc1", "Tier 1", "Critical Review"],
  ["tc2", "Tier 2", "Elevated Risk"],
  ["tc3", "Tier 3", "Emerging Risk"],
  ["tc4", "Tier 4", "Stable / Monitor"],
];

const enables = [
  "identifying the account for review",
  "assigning an appropriate risk segment",
  "initiating policy-approved engagement",
  "documenting the borrower's circumstances",
  "evaluating available options",
  "escalating when required",
  "monitoring the resulting outcome",
];

const phases = [
  ["Days 1–7", "Foundation"],
  ["Days 8–14", "Data & Scoring"],
  ["Days 15–30", "Setup & Pilot"],
  ["Days 31–60", "Expansion"],
  ["Days 61–90", "Performance Review & Scale"],
];

const governanceResp = ["Legal interpretation", "Compliance approval", "Model-risk review", "Fair-lending analysis", "Data governance", "Records management", "Investor requirements", "Servicing policy", "Staff training", "Ongoing monitoring"];

const scrollTo = (id) => { const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: "smooth" }); };

const FrameworkSection = () => (
  <>
    {/* 4-TIER CONCEPT */}
    <section id="framework" className="section bg1">
      <div className="w">
        <div style={{ maxWidth: 760 }}>
          <div className="eyebrow">The Framework</div>
          <h2 className="h2">A Structured 4-Tier Borrower Risk <em className="gold">Segmentation Framework</em></h2>
          <p className="lead">The Playbook provides a quantitative framework for organizing approved borrower information into four intervention tiers. Each tier is associated with defined review priorities, engagement strategies, documentation requirements, escalation considerations, and measurement objectives.</p>
        </div>
        <div className="tier-concept">
          {tiers.map((t, i) => (
            <div className={`tc ${t[0]}`} key={i}>
              <div className="tc-bar" />
              <div className="tc-t">{t[1]}</div>
              <div className="tc-n">{t[2]}</div>
            </div>
          ))}
        </div>
        <p className="md-note">The licensed Playbook contains the complete scoring methodology, factor tables, tier thresholds, refresh protocols, and worked borrower cases. Proprietary point values, weightings, and assignment rules are not published publicly.</p>
        <div className="hero-dual-cta" style={{ justifyContent: "flex-start", marginTop: "1.5rem" }}>
          <button className="btn-hero-secondary" onClick={() => scrollTo("preview")}>Request the Institutional Preview</button>
        </div>
      </div>
    </section>

    {/* WHAT THE FRAMEWORK ENABLES */}
    <section className="section bg2">
      <div className="wm">
        <div className="eyebrow">How It Works — At a High Level</div>
        <h2 className="h2">Example: What the Framework <em className="gold">Enables</em></h2>
        <p className="lead">A borrower who is still current may nevertheless exhibit approved indicators of changing payment capacity. The Playbook gives servicing teams a structured method for:</p>
        <div style={{ marginTop: "1.5rem", maxWidth: 640 }}>
          {enables.map((e, i) => (<div className="result-tick" key={i}><span>{e}</span></div>))}
        </div>
        <p className="md-note">The complete scoring methodology, tier-specific protocols, communication assets, scripts, and case studies are included in the institutional license.</p>
      </div>
    </section>

    {/* DIFFERENTIATION */}
    <section className="section bg1">
      <div className="wm">
        <div className="eyebrow">Strategic Differentiation</div>
        <h2 className="h2">Not Another Servicing <em className="gold">Software Platform.</em></h2>
        <p className="lead">Mortgage Survival Playbook 2026 is designed to complement — not replace — an institution's existing servicing, CRM, analytics, loss-mitigation, and investor systems.</p>
        <div className="compliance-strip" style={{ marginTop: "1.5rem" }}>
          {["Risk identification", "Prioritization", "Intervention", "Documentation", "Escalation", "Measurement"].map((t, i) => (<div className="cb" key={i}>{t}</div>))}
        </div>
        <p className="lead">The institution continues to control its own systems, data, legal interpretation, investor requirements, and servicing policies. This distinction is important.</p>
      </div>
    </section>

    {/* 90-DAY */}
    <section id="implementation" className="section bg2">
      <div className="w">
        <div style={{ maxWidth: 760, marginBottom: "1rem" }}>
          <div className="eyebrow">From Framework to Operation</div>
          <h2 className="h2">A 90-Day <em className="gold">Implementation Roadmap</em></h2>
        </div>
        <div className="info-grid">
          {phases.map((p, i) => (
            <div className="phase-card" key={i}>
              <div className="pc-head">{p[0]}</div>
              <div className="pc-txt"><strong>{p[1]}</strong></div>
            </div>
          ))}
        </div>
        <p className="md-note">The licensed Playbook contains the complete day-by-day implementation timeline, operational checklists, role assignments, and deployment guidance.</p>
      </div>
    </section>

    {/* GOVERNANCE */}
    <section id="governance" className="section bg1">
      <div className="w">
        <div style={{ maxWidth: 760 }}>
          <div className="eyebrow">Governance</div>
          <h2 className="h2">Designed for Institutional <em className="gold">Governance Review</em></h2>
          <p className="lead">The Playbook organizes operational checkpoints that institutions can map to applicable servicing requirements and internal controls. The licensed framework includes Regulation X reference materials, fair-lending governance resources, documentation guidance, and review checklists.</p>
        </div>
        <div className="comp-box" style={{ marginTop: "2rem" }}>
          <h4>Every institution remains responsible for</h4>
          <div className="comp-list" style={{ columns: 2 }}>
            {governanceResp.map((r, i) => (<div key={i}>· {r}</div>))}
          </div>
        </div>
        <p className="md-note">Designed to support internal review. The Playbook is not regulatory certification or legal advice, and does not itself constitute Regulation X or fair-lending compliance.</p>
      </div>
    </section>
  </>
);

export default FrameworkSection;
